import { Component, OnInit, ViewChild, TemplateRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { Subject } from 'rxjs';

import { CoreService } from '../../../services/core.service';
import { ManageApplicationNavComponent } from '../manage-application-nav/manage-application-nav.component';
import { CompanyFormComponent } from '../../../components/forms/create-account/company-form/company-form.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';

@Component({
  selector: 'app-index-accounts',
  standalone: true,
  imports: [
    CommonModule,
    MatPaginatorModule,
    MatTableModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatSortModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatDialogModule,
    ManageApplicationNavComponent,
    CompanyFormComponent
  ],
  templateUrl: './index-accounts.component.html',
  styleUrl: './index-accounts.component.scss'
})
export class ManageApplicationIndexAccountsComponent implements OnInit, OnDestroy {
  @ViewChild('companyFormTmpl', { static: true }) companyFormTmpl!: TemplateRef<any>;
  @ViewChild('confirmDeleteTmpl', { static: true }) confirmDeleteTmpl!: TemplateRef<any>;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['ID', 'created_at', 'pricing_plan', 'Name', 'actions'];

  searchValue: string = "";
  totalItems = 0;
  pageSize = 12;
  currentPage = 1;
  isLoading = true;
  hasLoaded = false;
  selectedCompany: any = {};

  sortField: string = 'id';
  sortDirection: string = 'desc';

  private destroy$ = new Subject<void>();

  constructor(
    private coreService: CoreService,
    public dialog: MatDialog
  ) { }

  ngOnInit() {
    this.loadAccounts();
  }

  loadAccounts(page: number = 1) {
    this.isLoading = true;

    this.coreService
      .getData('/manageAccount/all-companies/index', {
        page,
        keyword: this.searchValue,
        sort: this.sortField,
        direction: this.sortDirection,
        per_page: this.pageSize
      })
      .subscribe({
        next: (res: any) => {
          const companies = res.data || [];

          // All required data should now be included in `companies`
          this.dataSource.data = companies;
          this.totalItems = res.total || 0;
          this.pageSize = res.per_page || 12;
          this.currentPage = res.current_page || page;
          this.hasLoaded = true;

          if (this.paginator) {
            this.paginator.pageIndex = this.currentPage - 1;
            this.paginator.length = this.totalItems;
            this.paginator.pageSize = this.pageSize;
          }

          this.isLoading = false;
        },
        error: (err) => {
          console.error('Load accounts error:', err);
          this.dataSource.data = [];
          this.totalItems = 0;
          this.currentPage = 1;
          this.hasLoaded = true;
          this.isLoading = false;
        }
      });
  }

  onSearchSubmit(value: string) {
    this.searchValue = value.trim();
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadAccounts(1);
  }

  clearSearch() {
    this.searchValue = "";
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadAccounts(1);
  }

  onPageChange(event: any) {
    const newPage = event.pageIndex + 1;
    this.currentPage = newPage;
    this.loadAccounts(newPage);
  }

  onSortChange(sort: Sort) {
    this.sortField = sort.active === 'ID' ? 'id' : sort.active === 'Name' ? 'name' : sort.active;
    this.sortDirection = sort.direction || 'desc';
    this.currentPage = 1;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    this.loadAccounts(1);
  }

  addNewAccount() {
    this.selectedCompany = {};

    this.dialog.open(DialogComponent, {
      data: {
        content: this.companyFormTmpl,
        header: 'Add New Company',
        cls: '!bg-slate-600 !text-white',
      },
      width: '500px',
      maxWidth: '500px',
    });
  }

  editAccount(row: any) {
    this.selectedCompany = row;

    this.dialog.open(DialogComponent, {
      data: {
        content: this.companyFormTmpl,
        header: 'Edit Company',
        cls: '!bg-slate-600 !text-white',
      },
      width: '500px',
      maxWidth: '500px',
      maxHeight: '90vh'
    });
  }

  previewIndex(row: any) {
    window.location.href = `/manage-account/index-users/${row.ID}`;
  }

  openDeleteConfirm(row: any) {
    this.selectedCompany = row;

    this.dialog.open(DialogComponent, {
      data: {
        content: this.confirmDeleteTmpl,
        header: 'Delete Company?',
        cls: 'bg-red-500 !text-white',
      },
    });
  }

  deleteAccount() {
    this.coreService
      .deleteData('manageAccount/company/delete', String(this.selectedCompany.ID))
      .subscribe({
        next: () => {
          this.dialog.closeAll();
          this.coreService.showSnackbar('Company deleted successfully');
          this.loadAccounts(this.currentPage);
        },
        error: (err) => {
          console.error('Delete company error:', err);
          this.dialog.closeAll();
          this.coreService.showSnackbar('You cannot delete this company (it may have published posts)');
        }
      });
  }

  onSubmitCompanyClbk() {
    this.dialog.closeAll();
    this.loadAccounts(this.currentPage);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
